// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One maintenance event opened up: who recorded it, and each line — action, position,
 * quantity, before and after, a substitute material marked (design doc §10.6.1).
 *
 * Shared by the change log, the version comparison and the version timeline, which all
 * open an event in place; the detail is fetched on first open and kept, since an event
 * never changes once written (§7.6).
 *
 * `compact` is for the timeline, a column about 210px wide: five table columns do not fit
 * there, so each line stacks its action and position over "before → after".
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/endpoints";
import type { MaintenanceEvent, MaintenanceLine } from "@/api/types";
import { materialOf, summarizePart } from "@/archive/partState";
import { ErrorBanner } from "./ErrorBanner";

export function EventDetail({
  archiveId,
  event,
  compact = false,
}: {
  archiveId: string;
  event: Pick<MaintenanceEvent, "event_id" | "submitted_at">;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const detail = useQuery({
    queryKey: ["maintenance-event", archiveId, event.event_id],
    queryFn: () => api.maintenance.get(archiveId, event.event_id),
    // An event never changes once written (§7.6), so a detail fetched once is the detail.
    staleTime: Number.POSITIVE_INFINITY,
  });

  return (
    <div className="event-detail">
      <p className="muted event-detail-meta">
        {/* Who, then when: the submitter is the thing a reader cannot get from the list.
            Known only once the detail has loaded, so the time stands alone until then. */}
        {detail.data && (
          <>
            {t("maintenance.submittedBy")}:{" "}
            <span className="event-submitter">{detail.data.submitted_by_email ?? "—"}</span>
            {" · "}
          </>
        )}
        {t("maintenance.submittedAt")}: {new Date(event.submitted_at).toLocaleString()}
      </p>
      <ErrorBanner error={detail.error} />
      {detail.isLoading && <p className="muted">{t("common.loading")}</p>}
      {detail.data && compact && (
        <ul className="event-lines-compact">
          {detail.data.lines.map((line) => (
            <CompactLine key={line.line_no} line={line} />
          ))}
        </ul>
      )}
      {detail.data && !compact && (
        <table className="event-lines">
          <thead>
            <tr>
              <th>{t("maintenance.action")}</th>
              <th>{t("archive.position")}</th>
              <th>{t("archive.quantity")}</th>
              <th>{t("compare.before")}</th>
              <th>{t("compare.after")}</th>
            </tr>
          </thead>
          <tbody>
            {detail.data.lines.map((line) => (
              <LineRow key={line.line_no} line={line} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CompactLine({ line }: { line: MaintenanceLine }) {
  const { t } = useTranslation();
  const before = summarizePart(line.before);
  const after = summarizePart(line.after);
  const materialBefore = materialOf(line.before);
  const materialAfter = materialOf(line.after);

  return (
    <li>
      <div>
        <span className="badge">{t(`maintenance.${line.action.toLowerCase()}`)}</span>{" "}
        {line.position_name ?? "—"}
        {line.quantity !== 1 && <span className="muted"> ×{line.quantity}</span>}
      </div>
      {/* Before over after: side by side, a serial and its material name do not fit. */}
      <div className="event-line-change">
        <span className="event-line-arrow muted">{t("compare.before")}</span>
        <span>
          <PartCell value={before} material={materialBefore} />
        </span>
        <span className="event-line-arrow muted">{t("compare.after")}</span>
        <span>
          <PartCell
            value={after}
            material={materialAfter}
            valueChanged={after !== before}
            materialChanged={materialBefore !== materialAfter}
          />
        </span>
      </div>
    </li>
  );
}

function LineRow({ line }: { line: MaintenanceLine }) {
  const { t } = useTranslation();
  const before = summarizePart(line.before);
  const after = summarizePart(line.after);
  const materialBefore = materialOf(line.before);
  const materialAfter = materialOf(line.after);
  // Marked rather than left for the reader to spot: a substitute material on a replacement
  // (§8.6.1) is the single most important thing a maintenance line can say, and it reads
  // exactly like an ordinary swap unless something points at it.
  const materialChanged = materialBefore !== materialAfter;

  return (
    <tr>
      <td>
        <span className="badge">{t(`maintenance.${line.action.toLowerCase()}`)}</span>
      </td>
      <td>
        {line.position_name ?? "—"}
        {line.position_code && <span className="muted mono"> {line.position_code}</span>}
      </td>
      <td>{line.quantity}</td>
      <td>
        <PartCell value={before} material={materialBefore} />
      </td>
      <td>
        <PartCell
          value={after}
          material={materialAfter}
          valueChanged={after !== before}
          materialChanged={materialChanged}
        />
      </td>
    </tr>
  );
}

export function PartCell({
  value,
  material,
  valueChanged = false,
  materialChanged = false,
}: {
  value: string;
  material: string | null;
  valueChanged?: boolean;
  materialChanged?: boolean;
}) {
  return (
    <>
      <span className={valueChanged ? "serial is-changed" : "serial"}>{value}</span>
      {material && (
        <span className={materialChanged ? "part-material is-changed" : "part-material"}>
          {material}
        </span>
      )}
    </>
  );
}
